import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useBlocker, type Blocker } from 'react-router-dom';
import { useAuthSession } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';

/**
 * Epic #228 P4 (PD11): what a scope that claims blocked navigations gets.
 * `proceed` lets the navigation through, `reset` cancels it.
 * `hasOtherDirtyScopes` says whether a scope without a handler (a form that
 * relies on the shell dialog) is dirty too, so the handler can ask instead
 * of leaving silently.
 */
export interface NavBlockedNavigation {
  readonly proceed: () => void;
  readonly reset: () => void;
  readonly hasOtherDirtyScopes: () => boolean;
}

export interface NavBlockerOptions {
  /**
   * Owns the decision for a navigation blocked while this scope is dirty:
   * the shell's unsaved-changes dialog stays closed and this handler calls
   * `proceed` or `reset`. Without it the shell dialog asks, as before.
   */
  readonly onBlocked?: (navigation: NavBlockedNavigation) => void;
}

type BlockedHandlerRef = { current: NavBlockerOptions['onBlocked'] };

interface NavBlockerContextValue {
  blocker: Blocker;
  /** A dirty scope with an `onBlocked` handler owns the current block. */
  claimed: boolean;
  setScopeDirty: (scopeId: symbol, isDirty: boolean, handlerRef?: BlockedHandlerRef) => void;
  clearAllDirtyScopes: () => void;
}

const NavBlockerContext = createContext<NavBlockerContextValue | null>(null);

export function NavBlockerProvider({ children }: { children: ReactNode }) {
  const [isDirty, setIsDirtyState] = useState(false);
  // Dirty scopes, each with its `onBlocked` handler ref (null: the shell dialog asks).
  const dirtyScopesRef = useRef(new Map<symbol, BlockedHandlerRef | null>());
  // Epic #228 P4: some dirty scope claims blocked navigations. Kept as state
  // next to `isDirty`, so the shell dialog knows during render and never
  // flashes open for a block a handler owns.
  const [claimable, setClaimable] = useState(false);
  const { session } = useAuthSession(supabase);
  const sessionActive = session != null;

  // F-027 Phase 4.5 FIX 1 — keep an `isDirtyRef` in lock-step with the
  // isDirty state. React Router's useBlocker(fn) re-invokes `fn` on each
  // navigation attempt, reading whatever the current closure resolves.
  // By having `fn` read `isDirtyRef.current`, a caller can flip the ref
  // synchronously (from `ctx.setIsDirty(false)` in a submit handler,
  // just before awaiting mutateAsync → navigate()) and the very next
  // navigation attempt will see the updated value without waiting for a
  // re-render. State is kept in parallel so render-path consumers that
  // observe `isDirty` via context still work.
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  // §4.6 — once the session is gone, nav must flow freely so AuthGuard's
  // <Navigate to="/auth/signin"/> is NOT intercepted. We also proactively
  // release any in-flight blocker state and short-circuit new registrations
  // to false. SIGNED_OUT always wins over dirty-form guard.
  const publishDirtyState = useCallback((nextIsDirty: boolean) => {
    isDirtyRef.current = nextIsDirty;
    setIsDirtyState(nextIsDirty);
    setClaimable([...dirtyScopesRef.current.values()].some((handler) => handler !== null));
  }, []);

  const setScopeDirty = useCallback(
    (scopeId: symbol, scopeIsDirty: boolean, handlerRef?: BlockedHandlerRef) => {
      if (!sessionActive) {
        dirtyScopesRef.current.clear();
        publishDirtyState(false);
        return;
      }
      if (scopeIsDirty) {
        dirtyScopesRef.current.set(scopeId, handlerRef ?? null);
      } else {
        dirtyScopesRef.current.delete(scopeId);
      }
      publishDirtyState(dirtyScopesRef.current.size > 0);
    },
    [publishDirtyState, sessionActive],
  );

  const clearAllDirtyScopes = useCallback(() => {
    dirtyScopesRef.current.clear();
    publishDirtyState(false);
  }, [publishDirtyState]);

  const unregisterScope = useCallback(
    (scopeId: symbol) => {
      dirtyScopesRef.current.delete(scopeId);
      publishDirtyState(dirtyScopesRef.current.size > 0);
    },
    [publishDirtyState],
  );

  // C4 — predicate is plain `isDirty && sessionActive`. No pathname-equal
  // short-circuit so dirty same-route sidebar clicks still fire the dialog
  // and `?fix=contact`-style search-string round-trips are not wiped. The
  // predicate reads isDirtyRef.current so a synchronous flip (FIX 1) is
  // visible immediately.
  const blocker = useBlocker(() => isDirtyRef.current && sessionActive);
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;
  // A block handed to a handler stays claimed until it ends, even when the
  // scope turns clean first (its flush succeeded just before `proceed`).
  const [handling, setHandling] = useState(false);
  const claimed = sessionActive && (claimable || handling);

  // Epic #228 P4 (PD11): a block a dirty scope claims goes to its handler,
  // once per block. The latest-registered claiming scope decides; scopes
  // without a handler are reported through `hasOtherDirtyScopes`.
  const handledBlockRef = useRef(false);
  useEffect(() => {
    if (blocker.state !== 'blocked') {
      handledBlockRef.current = false;
      setHandling(false);
      return;
    }
    if (!claimed || handledBlockRef.current) return;
    const owners = [...dirtyScopesRef.current.values()].filter(
      (handler): handler is BlockedHandlerRef => handler !== null,
    );
    const handler = owners[owners.length - 1]?.current;
    if (handler === undefined) return;
    handledBlockRef.current = true;
    setHandling(true);
    handler({
      proceed: () => {
        if (blockerRef.current.state === 'blocked') blockerRef.current.proceed?.();
      },
      reset: () => {
        if (blockerRef.current.state === 'blocked') blockerRef.current.reset?.();
      },
      hasOtherDirtyScopes: () =>
        [...dirtyScopesRef.current.values()].some((scopeHandler) => scopeHandler === null),
    });
  }, [blocker.state, claimed]);

  // Browser close/reload cannot be intercepted by React Router. Register a
  // native beforeunload guard while the authenticated admin shell has any
  // dirty scope, so tab closes get the browser's unsaved-changes prompt.
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!sessionActive || !isDirtyRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [sessionActive]);

  // §4.6 — if the session drops while a nav is already blocked, proceed
  // through it so AuthGuard can redirect to /auth/signin without the dialog
  // holding the merchant on a protected route.
  useEffect(() => {
    if (!sessionActive && blocker.state === 'blocked') {
      blocker.proceed?.();
    }
  }, [sessionActive, blocker]);

  // §4.6 — when the session drops, also clear the dirty flag defensively
  // so any re-render with a stale isDirty prop still reads clean.
  useEffect(() => {
    if (!sessionActive) {
      dirtyScopesRef.current.clear();
      publishDirtyState(false);
    }
  }, [publishDirtyState, sessionActive]);

  const value = useMemo<NavBlockerContextValue>(
    () => ({ blocker, claimed, setScopeDirty, clearAllDirtyScopes }),
    [blocker, claimed, clearAllDirtyScopes, setScopeDirty],
  );

  return (
    <NavBlockerContext.Provider value={value}>
      <NavBlockerScopeCleanup.Provider value={unregisterScope}>
        {children}
      </NavBlockerScopeCleanup.Provider>
    </NavBlockerContext.Provider>
  );
}

const NavBlockerScopeCleanup = createContext<((scopeId: symbol) => void) | null>(null);

/** Single sanctioned API: any form inside the admin shell calls
 *  `useNavBlocker(formState.isDirty || extraDirty)` inside its body.
 *  Epic #228 P4: `options.onBlocked` lets the caller decide a blocked
 *  navigation itself (the shell dialog then stays closed). */
// eslint-disable-next-line react-refresh/only-export-components
export function useNavBlocker(isDirty: boolean, options?: NavBlockerOptions): void {
  const ctx = useContext(NavBlockerContext);
  const unregisterScope = useContext(NavBlockerScopeCleanup);
  if (!ctx) throw new Error('useNavBlocker must be used inside <AdminShellLayout/>');
  if (!unregisterScope) throw new Error('useNavBlocker must be used inside <AdminShellLayout/>');

  const scopeIdRef = useRef<symbol | null>(null);
  if (!scopeIdRef.current) scopeIdRef.current = Symbol('nav-blocker-scope');
  // The latest handler, read when a navigation is blocked. Only its presence
  // is registered (and re-registered when it appears or goes), so a new
  // callback identity each render never re-publishes the dirty flag.
  const handlerRef = useRef<BlockedHandlerRef['current']>(options?.onBlocked);
  handlerRef.current = options?.onBlocked;
  const claims = options?.onBlocked !== undefined;

  // FIX 1 refinement — depend only on the stable `setIsDirty` callback,
  // NOT on `ctx`. The context value object identity changes on every
  // provider re-render (because `blocker` gets a fresh shape from
  // `useBlocker` on each render of the provider), which would otherwise
  // cause this effect to re-fire and re-publish `isDirty=true` right
  // after a submit handler called `markSettled()` — resetting the
  // isDirtyRef back to true before the route's programmatic navigate()
  // reached the blocker. `setIsDirty` is wrapped in `useCallback` with
  // only `sessionActive` as a dep, so its identity is stable across
  // ordinary renders and the effect only re-runs when the caller's
  // `isDirty` arg actually changes.
  const { setScopeDirty } = ctx;
  useEffect(() => {
    const scopeId = scopeIdRef.current;
    if (!scopeId) return;
    setScopeDirty(scopeId, isDirty, claims ? handlerRef : undefined);
    return () => {
      unregisterScope(scopeId);
    };
  }, [claims, isDirty, setScopeDirty, unregisterScope]);
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNavBlockerState(): Blocker {
  const ctx = useContext(NavBlockerContext);
  if (!ctx) throw new Error('useNavBlockerState must be used inside <AdminShellLayout/>');
  return ctx.blocker;
}

/** Epic #228 P4: a dirty scope with an `onBlocked` handler owns blocked navigations. */
// eslint-disable-next-line react-refresh/only-export-components
export function useNavBlockerClaimed(): boolean {
  const ctx = useContext(NavBlockerContext);
  if (!ctx) throw new Error('useNavBlockerClaimed must be used inside <AdminShellLayout/>');
  return ctx.claimed;
}

/**
 * Imperative escape-valve for forms that navigate programmatically on a
 * successful submit. The sanctioned flow is:
 *
 *     const markSettled = useNavBlockerMarkSettled();
 *     ...
 *     const onSubmit = async (values) => {
 *       markSettled();         // blocker predicate flips to false now
 *       await mutate(values);  // parent route navigate() flows through
 *     };
 *
 * `markSettled()` writes to the provider's isDirtyRef synchronously, so
 * the very next navigation attempt sees a false predicate — even though
 * RHF's formState.isDirty won't flip until the next render. Without this
 * escape valve, the blocker intercepts post-submit programmatic navigation
 * and the app stays on the source route.
 *
 * This does NOT affect the underlying `useNavBlocker(isDirty)` effect
 * loop — the next render from the consumer will re-publish `isDirty` into
 * the provider, so if the submission fails or the form is re-dirtied, the
 * blocker re-engages automatically.
 *
 * See F-027 Phase 4.5 fix receipt.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useNavBlockerMarkSettled(): () => void {
  const ctx = useContext(NavBlockerContext);
  if (!ctx) {
    throw new Error('useNavBlockerMarkSettled must be used inside <AdminShellLayout/>');
  }
  return useCallback(() => {
    ctx.clearAllDirtyScopes();
  }, [ctx]);
}
